/**
 * @file SystemHubModalVendas.js
 * @description Hub Comercial / Pipeline de Vendas — BLB System.
 *
 * Refino: ARQUIVAR fechados (reversível) + FILTRO POR MÊS na coluna Fechado.
 *   - O filtro de mês governa APENAS a coluna "Fechado (ganho)". Leads em aberto
 *     (novo → negociação) aparecem sempre — nunca somem por causa do mês.
 *   - Contador "(N em outros meses)" deixa explícito que nada foi perdido.
 *   - Arquivar por card ou em lote (fechados do mês); arquivados ficam ocultos,
 *     mas acessíveis pelo toggle "Arquivados (n)". Tudo reversível.
 *
 * Consome (novos):
 *   PUT  /api/vendas/lead/:id/arquivar        { arquivado: bool }
 *   POST /api/vendas/leads/arquivar-lote      { ids: [], arquivado: bool }
 * (o dashboard deve retornar os campos `arquivado` e `data_ganho`.)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './SystemHubModalVendas.css';
import LeadCentralModal from './LeadCentralModal';
import {
  FaPlus, FaSearch, FaTimes, FaDollarSign, FaUserTie, FaCheckCircle,
  FaFire, FaSnowflake, FaSmog, FaChevronLeft, FaChevronRight, FaGripVertical,
  FaHistory, FaPaperPlane, FaRegStickyNote, FaSms, FaEnvelope, FaWhatsapp,
  FaInfoCircle, FaColumns, FaListUl, FaFilter, FaBolt, FaExclamationTriangle,
  FaPercentage, FaFileSignature, FaArchive, FaBoxOpen, FaCalendarAlt,
} from 'react-icons/fa';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURAÇÃO
// ─────────────────────────────────────────────────────────────────────────────
const STAGES = {
  novo:          { label: 'Novos leads',      color: '#579bfc' },
  contato:       { label: 'Em contato',       color: '#a25ddc' },
  proposta:      { label: 'Proposta enviada', color: '#f5a623' },
  negociacao:    { label: 'Em negociação',    color: '#e2445c' },
  fechado_ganho: { label: 'Fechado (ganho)',  color: '#00c875' },
};
const STAGE_ORDER = ['novo', 'contato', 'proposta', 'negociacao', 'fechado_ganho'];

const ESTAGIOS_CONVERSAO_LIBERADA = new Set(['negociacao', 'fechado_ganho']);

const TEMPS = {
  quente: { label: 'Quente', icon: FaFire,      cls: 'cmx-temp--hot' },
  morno:  { label: 'Morno',  icon: FaSmog,      cls: 'cmx-temp--warm' },
  frio:   { label: 'Frio',   icon: FaSnowflake, cls: 'cmx-temp--cold' },
};

const STATUS_LABELS = { ganho: 'Ganho', perdido: 'Perdido', convertido: 'Convertido' };

const STALE_DAYS = 5;

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const formatCurrency = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getInitials = (n) =>
  (n || '').split(' ').filter(Boolean).map((x) => x[0]).slice(0, 2).join('').toUpperCase() || '?';

const daysSince = (dateValue) => {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
};

const formatDateTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const onlyDigits = (s) => (s || '').toString().replace(/\D/g, '');
const waLink = (phone) => `https://wa.me/55${onlyDigits(phone)}`;

// ── Helpers de arquivamento / mês ──
const closedDate = (l) => l?.data_ganho || l?.data_atualizacao || null;
const monthKeyOf = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const MESES_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const monthLabelOf = (key) => {
  if (!key || key === 'todos') return 'Todos os meses';
  const [y, m] = key.split('-');
  return `${MESES_PT[Number(m) - 1]}/${y}`;
};
const isArquivado = (l) => Number(l?.arquivado) === 1 || l?.arquivado === true;
const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// CSS só dos elementos novos (o restante segue no SystemHubModalVendas.css).
const CMX_EXTRA_CSS = `
.cmx-monthsel{display:inline-flex;align-items:center;gap:7px;border:1px solid #e6e8f0;border-radius:10px;padding:0 12px;background:#fff;color:#6b7280}
.cmx-monthsel select{border:none;outline:none;background:transparent;padding:9px 0;font-size:.85rem;font-weight:600;color:#1a2130;cursor:pointer;font-family:inherit}
.cmx-card__archive{border:none;background:transparent;color:#9ca3af;cursor:pointer;padding:3px;border-radius:6px;display:grid;place-items:center;transition:.15s}
.cmx-card__archive:hover{background:#eef2f7;color:#0d7377}
.cmx-card--archived{opacity:.62;background:repeating-linear-gradient(135deg,#fafbfc,#fafbfc 8px,#f3f5f8 8px,#f3f5f8 16px)}
.cmx-fechado-tools{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:8px}
.cmx-fechado-btn{display:inline-flex;align-items:center;gap:5px;font-size:.72rem;font-weight:700;cursor:pointer;border:1px solid #e6e8f0;background:#fff;color:#4b5563;padding:4px 9px;border-radius:8px;transition:.15s;font-family:inherit}
.cmx-fechado-btn:hover{border-color:#0d7377;color:#0d7377;background:#f2fbfa}
.cmx-fechado-btn.is-on{border-color:#0d7377;color:#fff;background:#0d7377}
.cmx-fechado-hint{font-size:.7rem;color:#9ca3af;font-weight:600}
`;

// ─────────────────────────────────────────────────────────────────────────────
// KPI CARD
// ─────────────────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, tone }) {
  return (
    <div className={`cmx-kpi cmx-kpi--${tone}`}>
      <div className="cmx-kpi__icon">{icon}</div>
      <div className="cmx-kpi__data">
        <span className="cmx-kpi__value">{value}</span>
        <span className="cmx-kpi__label">{label}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMPERATURA
// ─────────────────────────────────────────────────────────────────────────────
function TempBadge({ temperatura }) {
  const t = TEMPS[temperatura] || TEMPS.morno;
  const Icon = t.icon;
  return (
    <span className={`cmx-temp ${t.cls}`} title={`Lead ${t.label}`}>
      <Icon />
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LEAD CARD (Kanban)
// ─────────────────────────────────────────────────────────────────────────────
function LeadCard({ lead, index, onOpen, onMove, onArchive }) {
  const stale = daysSince(lead.data_atualizacao);
  const isStale = stale !== null && stale >= STALE_DAYS && lead.estagio !== 'fechado_ganho';
  const statusLabel = lead.status && STATUS_LABELS[lead.status];
  const isClosed = lead.estagio === 'fechado_ganho';
  const arquivada = isArquivado(lead);

  return (
    <Draggable draggableId={String(lead.id)} index={index}>
      {(provided, snapshot) => {
        const card = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`cmx-card ${snapshot.isDragging ? 'cmx-card--dragging' : ''} ${arquivada ? 'cmx-card--archived' : ''}`}
            onClick={() => !snapshot.isDragging && onOpen(lead)}
          >
            <button
              type="button"
              className="cmx-card__nav cmx-card__nav--left"
              disabled={lead.estagio === STAGE_ORDER[0]}
              title="Voltar etapa"
              onClick={(e) => { e.stopPropagation(); onMove(lead.id, lead.estagio, 'prev'); }}
            >
              <FaChevronLeft />
            </button>

            <span className="cmx-card__bar" style={{ background: STAGES[lead.estagio]?.color }} />

            <div className="cmx-card__body">
              <div className="cmx-card__top">
                <span className="cmx-card__company">{lead.nome_empresa || 'Particular'}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <TempBadge temperatura={lead.temperatura} />
                  {isClosed && (
                    <button
                      type="button"
                      className="cmx-card__archive"
                      title={arquivada ? 'Desarquivar' : 'Arquivar'}
                      onClick={(e) => { e.stopPropagation(); onArchive(lead.id, !arquivada); }}
                    >
                      {arquivada ? <FaBoxOpen /> : <FaArchive />}
                    </button>
                  )}
                </span>
              </div>

              <h4 className="cmx-card__name">{lead.nome_contato}</h4>

              <div className="cmx-card__meta">
                {Number(lead.score) > 0 && (
                  <span className="cmx-chip cmx-chip--score" title="Lead score"><FaBolt /> {lead.score}</span>
                )}
                {statusLabel && (
                  <span className={`cmx-chip cmx-chip--status cmx-chip--${lead.status}`}>{statusLabel}</span>
                )}
                {isStale && (
                  <span className="cmx-chip cmx-chip--stale" title="Sem interação recente">
                    <FaExclamationTriangle /> parado {stale}d
                  </span>
                )}
              </div>

              <div className="cmx-card__foot">
                <span className="cmx-card__value">{formatCurrency(lead.valor_potencial)}</span>
                <span className="cmx-card__owner" title={lead.responsavel_nome || 'Sem responsável'}>
                  {lead.responsavel_avatar
                    ? <img src={lead.responsavel_avatar} alt="" />
                    : <span className="cmx-avatar-mini">{getInitials(lead.responsavel_nome)}</span>}
                </span>
                <FaGripVertical className="cmx-card__grip" />
              </div>
            </div>

            <button
              type="button"
              className="cmx-card__nav cmx-card__nav--right"
              disabled={lead.estagio === STAGE_ORDER[STAGE_ORDER.length - 1]}
              title="Avançar etapa"
              onClick={(e) => { e.stopPropagation(); onMove(lead.id, lead.estagio, 'next'); }}
            >
              <FaChevronRight />
            </button>
          </div>
        );
        return snapshot.isDragging ? createPortal(card, document.body) : card;
      }}
    </Draggable>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LISTA
// ─────────────────────────────────────────────────────────────────────────────
function LeadTable({ leads, onOpen }) {
  if (leads.length === 0) {
    return <div className="cmx-empty"><FaInfoCircle /> Nenhum lead encontrado com os filtros atuais.</div>;
  }
  return (
    <div className="cmx-table-wrap">
      <table className="cmx-table">
        <thead>
          <tr>
            <th>Contato</th><th>Empresa</th><th>Etapa</th>
            <th className="cmx-num">Valor</th><th className="cmx-center">Temp.</th>
            <th className="cmx-center">Score</th><th>Responsável</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id} onClick={() => onOpen(lead)}>
              <td><strong>{lead.nome_contato}</strong></td>
              <td>{lead.nome_empresa || <span className="cmx-muted">Particular</span>}</td>
              <td>
                <span className="cmx-stage-pill" style={{ '--stage': STAGES[lead.estagio]?.color }}>
                  {STAGES[lead.estagio]?.label || lead.estagio}
                </span>
              </td>
              <td className="cmx-num">{formatCurrency(lead.valor_potencial)}</td>
              <td className="cmx-center"><TempBadge temperatura={lead.temperatura} /></td>
              <td className="cmx-center">{Number(lead.score) || 0}</td>
              <td>{lead.responsavel_nome || <span className="cmx-muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL: NOVO LEAD
// ─────────────────────────────────────────────────────────────────────────────
function NewLeadModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    nome_contato: '', nome_empresa: '', telefone: '', email: '',
    valor_potencial: '', temperatura: 'morno', origem: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome_contato.trim()) return toast.warn('Informe o nome do contato.');
    setSaving(true);
    try { await onCreate(form); onClose(); } finally { setSaving(false); }
  };

  return (
    <div className="cmx-modal-overlay" onClick={onClose}>
      <div className="cmx-modal" onClick={(e) => e.stopPropagation()}>
        <header className="cmx-modal__head">
          <h3>Nova oportunidade</h3>
          <button type="button" className="cmx-icon-btn" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
        </header>
        <form className="cmx-form" onSubmit={submit}>
          <div className="cmx-field">
            <label>Contato principal</label>
            <input autoFocus placeholder="Nome do cliente"
              value={form.nome_contato} onChange={(e) => set('nome_contato', e.target.value)} />
          </div>
          <div className="cmx-field">
            <label>Empresa</label>
            <input placeholder="Nome da empresa (ou deixe em branco p/ particular)"
              value={form.nome_empresa} onChange={(e) => set('nome_empresa', e.target.value)} />
          </div>
          <div className="cmx-row">
            <div className="cmx-field">
              <label>WhatsApp / Celular</label>
              <input placeholder="(00) 00000-0000"
                value={form.telefone} onChange={(e) => set('telefone', e.target.value)} />
            </div>
            <div className="cmx-field">
              <label>E-mail</label>
              <input type="email" placeholder="contato@empresa.com"
                value={form.email} onChange={(e) => set('email', e.target.value)} />
            </div>
          </div>
          <div className="cmx-row">
            <div className="cmx-field">
              <label>Valor estimado (R$)</label>
              <input type="number" min="0" step="0.01" placeholder="0,00"
                value={form.valor_potencial} onChange={(e) => set('valor_potencial', e.target.value)} />
            </div>
            <div className="cmx-field">
              <label>Origem</label>
              <input placeholder="Site, indicação, WhatsApp…"
                value={form.origem} onChange={(e) => set('origem', e.target.value)} />
            </div>
          </div>
          <div className="cmx-field">
            <label>Temperatura do lead</label>
            <div className="cmx-temp-select">
              {Object.entries(TEMPS).map(([key, t]) => {
                const Icon = t.icon;
                return (
                  <button key={key} type="button"
                    className={`cmx-temp-opt cmx-temp-opt--${key} ${form.temperatura === key ? 'is-active' : ''}`}
                    onClick={() => set('temperatura', key)}>
                    <Icon /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <footer className="cmx-modal__foot">
            <button type="button" className="cmx-btn cmx-btn--ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="cmx-btn cmx-btn--primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Criar lead'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SystemHubModalVendas() {
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState({ total_leads: 0, pipeline_total: 0, valor_fechado: 0 });
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState('kanban');
  const [search, setSearch] = useState('');
  const [fResp, setFResp] = useState('todos');
  const [fTemp, setFTemp] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);

  // Refino: mês da coluna Fechado + exibição de arquivados.
  const [monthFilter, setMonthFilter] = useState(currentMonthKey());
  const [showArquivados, setShowArquivados] = useState(false);

  const [newOpen, setNewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get('/api/vendas/dashboard');
      setLeads(Array.isArray(data.leads) ? data.leads : []);
      setMetrics(data.metrics || { total_leads: 0, pipeline_total: 0, valor_fechado: 0 });
    } catch {
      toast.error('Não foi possível carregar o pipeline comercial.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!selected) return;
    const atualizado = leads.find((l) => l.id === selected.id);
    if (atualizado && atualizado !== selected) setSelected(atualizado);
  }, [leads, selected]);

  const responsaveis = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => { if (l.responsavel_id) map.set(l.responsavel_id, l.responsavel_nome || `#${l.responsavel_id}`); });
    return Array.from(map, ([id, nome]) => ({ id, nome }));
  }, [leads]);

  // Meses disponíveis a partir dos fechados (+ mês atual sempre presente).
  const monthOptions = useMemo(() => {
    const set = new Set([currentMonthKey()]);
    leads.forEach((l) => {
      if (l.estagio === 'fechado_ganho') {
        const k = monthKeyOf(closedDate(l));
        if (k) set.add(k);
      }
    });
    return Array.from(set).sort().reverse();
  }, [leads]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (term && !(`${l.nome_contato || ''} ${l.nome_empresa || ''}`.toLowerCase().includes(term))) return false;
      if (fResp !== 'todos' && String(l.responsavel_id) !== String(fResp)) return false;
      if (fTemp !== 'todos' && l.temperatura !== fTemp) return false;
      return true;
    });
  }, [leads, search, fResp, fTemp]);

  const kpis = useMemo(() => {
    const ativos = leads.filter((l) => l.estagio !== 'fechado_ganho').length;
    const total = leads.length || 1;
    const ganhos = leads.filter((l) => l.estagio === 'fechado_ganho' || l.status === 'ganho' || l.status === 'convertido').length;
    return {
      ativos,
      forecast: metrics.pipeline_total,
      fechado: metrics.valor_fechado,
      conversao: Math.round((ganhos / total) * 100),
    };
  }, [leads, metrics]);

  const moveStage = useCallback(async (leadId, newStage) => {
    const snapshot = leads;
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, estagio: newStage } : l)));
    try {
      await api.put(`/api/vendas/lead/${leadId}/move`, { novo_estagio: newStage });
      if (newStage === 'fechado_ganho') toast.success('Negócio fechado! 🎉', { autoClose: 1200 });
    } catch {
      setLeads(snapshot);
      toast.error('Não foi possível mover o lead.');
    }
  }, [leads]);

  const moveStep = (leadId, current, dir) => {
    const i = STAGE_ORDER.indexOf(current);
    const j = dir === 'next' ? i + 1 : i - 1;
    if (j >= 0 && j < STAGE_ORDER.length) moveStage(leadId, STAGE_ORDER[j]);
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    moveStage(Number(draggableId), destination.droppableId);
  };

  // ── Arquivar (otimista + rollback) ──
  const archiveLead = useCallback(async (leadId, arquivar) => {
    const snapshot = leads;
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, arquivado: arquivar ? 1 : 0 } : l)));
    try {
      await api.put(`/api/vendas/lead/${leadId}/arquivar`, { arquivado: arquivar });
    } catch {
      setLeads(snapshot);
      toast.error(`Não foi possível ${arquivar ? 'arquivar' : 'desarquivar'} o lead.`);
    }
  }, [leads]);

  const bulkArchiveFechados = useCallback(async () => {
    const alvos = filtered
      .filter((l) => l.estagio === 'fechado_ganho' && !isArquivado(l)
        && (monthFilter === 'todos' || monthKeyOf(closedDate(l)) === monthFilter))
      .map((l) => l.id);
    if (!alvos.length) return toast.info('Nada para arquivar neste período.');
    if (!window.confirm(`Arquivar ${alvos.length} lead(s) fechado(s) de ${monthLabelOf(monthFilter)}? Você pode desarquivar depois.`)) return;

    const snapshot = leads;
    setLeads((prev) => prev.map((l) => (alvos.includes(l.id) ? { ...l, arquivado: 1 } : l)));
    try {
      await api.post('/api/vendas/leads/arquivar-lote', { ids: alvos, arquivado: true });
      toast.success(`${alvos.length} lead(s) arquivado(s).`);
    } catch {
      setLeads(snapshot);
      toast.error('Falha ao arquivar em lote.');
    }
  }, [filtered, leads, monthFilter]);

  const createLead = async (form) => {
    try {
      await api.post('/api/vendas/lead', {
        ...form,
        valor_potencial: form.valor_potencial ? Number(form.valor_potencial) : 0,
      });
      toast.success('Lead criado.');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível criar o lead.');
      throw e;
    }
  };

  const openLead = (lead) => { setSelected(lead); setDrawerOpen(true); };

  // ── Colunas: mês + arquivado só valem para Fechado. Abertas nunca somem. ──
  const columnLeads = (stage) => {
    let arr = filtered.filter((l) => l.estagio === stage);
    if (stage === 'fechado_ganho') {
      if (monthFilter !== 'todos') arr = arr.filter((l) => monthKeyOf(closedDate(l)) === monthFilter);
      arr = arr.filter((l) => (showArquivados ? true : !isArquivado(l)));
    } else {
      arr = arr.filter((l) => !isArquivado(l));
    }
    return arr;
  };
  const columnSum = (stage) =>
    columnLeads(stage).reduce((acc, l) => acc + Number(l.valor_potencial || 0), 0);

  // Métricas de apoio da coluna Fechado (transparência do filtro).
  const fechadoStats = useMemo(() => {
    const fechados = filtered.filter((l) => l.estagio === 'fechado_ganho');
    const naoArqTotal = fechados.filter((l) => !isArquivado(l));
    const noMes = (l) => monthFilter === 'todos' || monthKeyOf(closedDate(l)) === monthFilter;
    const ocultosOutrosMeses = monthFilter === 'todos'
      ? 0
      : naoArqTotal.length - naoArqTotal.filter(noMes).length;
    const arquivadosNoMes = fechados.filter((l) => isArquivado(l) && noMes(l)).length;
    return { ocultosOutrosMeses: Math.max(0, ocultosOutrosMeses), arquivadosNoMes };
  }, [filtered, monthFilter]);

  return (
    <div className="cmx-page">
      <style>{CMX_EXTRA_CSS}</style>

      {/* HEADER */}
      <header className="cmx-header">
        <div className="cmx-header__title">
          <h1><FaUserTie /> Comercial · Pipeline de vendas</h1>
          <p>Gestão do contato com cada lead até o fechamento — BLB System.</p>
        </div>
        <div className="cmx-header__actions">
          <button type="button" className="cmx-btn cmx-btn--primary" onClick={() => setNewOpen(true)}>
            <FaPlus /> Novo lead
          </button>
        </div>
      </header>

      {/* KPIs */}
      <section className="cmx-kpis">
        <KpiCard tone="blue"   icon={<FaUserTie />}     label="Leads ativos"      value={kpis.ativos} />
        <KpiCard tone="navy"   icon={<FaDollarSign />}  label="Forecast pipeline" value={formatCurrency(kpis.forecast)} />
        <KpiCard tone="green"  icon={<FaCheckCircle />} label="Ganhos (mês)"      value={formatCurrency(kpis.fechado)} />
        <KpiCard tone="amber"  icon={<FaPercentage />}  label="Taxa de conversão" value={`${kpis.conversao}%`} />
      </section>

      {/* TOOLBAR */}
      <section className="cmx-toolbar">
        <div className="cmx-search">
          <FaSearch />
          <input placeholder="Buscar por contato ou empresa…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <label className="cmx-monthsel" title="Filtra apenas a coluna Fechado (ganho)">
          <FaCalendarAlt />
          <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
            <option value="todos">Fechados: todos os meses</option>
            {monthOptions.map((k) => <option key={k} value={k}>Fechados: {monthLabelOf(k)}</option>)}
          </select>
        </label>

        <button type="button" className={`cmx-btn cmx-btn--ghost ${showFilters ? 'is-active' : ''}`}
          onClick={() => setShowFilters((s) => !s)}>
          <FaFilter /> Filtros
        </button>

        <div className="cmx-viewtoggle">
          <button className={view === 'kanban' ? 'is-active' : ''} onClick={() => setView('kanban')} title="Kanban">
            <FaColumns />
          </button>
          <button className={view === 'lista' ? 'is-active' : ''} onClick={() => setView('lista')} title="Lista">
            <FaListUl />
          </button>
        </div>
      </section>

      {showFilters && (
        <section className="cmx-filters">
          <label>
            Responsável
            <select value={fResp} onChange={(e) => setFResp(e.target.value)}>
              <option value="todos">Todos</option>
              {responsaveis.map((r) => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </label>
          <div className="cmx-temp-filter">
            <span>Temperatura</span>
            {['todos', 'quente', 'morno', 'frio'].map((t) => (
              <button key={t} className={`cmx-temp-pill ${fTemp === t ? 'is-active' : ''}`}
                onClick={() => setFTemp(t)}>
                {t === 'todos' ? 'Todas' : TEMPS[t].label}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* CONTEÚDO */}
      {loading ? (
        <div className="cmx-loading">Carregando pipeline…</div>
      ) : view === 'kanban' ? (
        <div className="cmx-board-wrap">
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="cmx-board">
              {STAGE_ORDER.map((stage) => {
                const isFechado = stage === 'fechado_ganho';
                return (
                  <div className="cmx-col" key={stage}>
                    <div className="cmx-col__head" style={{ '--stage': STAGES[stage].color }}>
                      <div className="cmx-col__title">
                        <span>{STAGES[stage].label}</span>
                        <span className="cmx-col__count">{columnLeads(stage).length}</span>
                      </div>
                      <span className="cmx-col__sum">{formatCurrency(columnSum(stage))}</span>

                      {isFechado && (
                        <div className="cmx-fechado-tools">
                          <button type="button" className="cmx-fechado-btn" onClick={bulkArchiveFechados}
                            title={`Arquivar os fechados de ${monthLabelOf(monthFilter)}`}>
                            <FaArchive /> Arquivar {monthFilter === 'todos' ? 'tudo' : 'mês'}
                          </button>
                          <button type="button" className={`cmx-fechado-btn ${showArquivados ? 'is-on' : ''}`}
                            onClick={() => setShowArquivados((s) => !s)}>
                            {showArquivados ? <FaBoxOpen /> : <FaArchive />}
                            {showArquivados ? 'Ocultar arquivados' : `Arquivados (${fechadoStats.arquivadosNoMes})`}
                          </button>
                          {fechadoStats.ocultosOutrosMeses > 0 && (
                            <span className="cmx-fechado-hint">{fechadoStats.ocultosOutrosMeses} em outros meses</span>
                          )}
                        </div>
                      )}
                    </div>

                    <Droppable droppableId={stage}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}
                          className={`cmx-col__body ${snapshot.isDraggingOver ? 'is-over' : ''}`}>
                          {columnLeads(stage).map((lead, index) => (
                            <LeadCard key={lead.id} lead={lead} index={index}
                              onOpen={openLead} onMove={moveStep} onArchive={archiveLead} />
                          ))}
                          {provided.placeholder}
                          {columnLeads(stage).length === 0 && (
                            <div className="cmx-col__empty">
                              {isFechado && monthFilter !== 'todos' ? 'Nenhum fechado neste mês' : 'Sem leads aqui'}
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </div>
      ) : (
        <LeadTable leads={filtered.filter((l) => !isArquivado(l))} onOpen={openLead} />
      )}

      {/* MODAIS / DRAWER */}
      {newOpen && <NewLeadModal onClose={() => setNewOpen(false)} onCreate={createLead} />}
      <LeadCentralModal
        lead={selected}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onAfterAction={fetchData}
      />
    </div>
  );
}